import { useEffect, useRef, useState } from 'react';

import { ActionIcon, Badge, Box, Button, Group, Menu, Pagination, ScrollArea, Tabs, Text, Textarea } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconArchive,
  IconArchiveOff,
  IconArrowsSort,
  IconBookmark,
  IconBookmarkOff,
  IconDotsVertical,
  IconPencil,
  IconPin
} from '@tabler/icons-react';

import {
  useArchiveNoteMutation,
  useCreateNoteMutation,
  useGetNotesByPatientIdQuery,
  useGetPinnedNotesQuery,
  useUnArchiveNoteMutation,
  useUpdateNoteMutation
} from '@/store/slices/api/clinicalNotesApiSlice';
import { NotesTabType } from '@/utils/constants';
import formatDate from '@/utils/formatDate';

import { ClinicalNote, NoteSource } from '@/types/clinicalNote';
import ConfirmationModal from '@/ui/ConfirmationModal';
import SkeletonGrid from '@/ui/Skeleton';
import TextWrapper from '@/ui/TextWrapper';

import AddNotesModal from './AddNotesModal';
import classes from './ClinicalNotes.module.scss';

interface ClinicalNotesProps {
  patientId: string;
  onlyPinnedNotes?: boolean;
}

const AVATAR_COLORS = ['#0d9488', '#6366f1', '#7c3aed', '#0891b2', '#d97706', '#db2777'];

const avatarColor = (key: string) => {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = key.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const personName = (user?: { given?: string | null; family?: string | null; email?: string | null } | null) =>
  [user?.given, user?.family].filter(Boolean).join(' ').trim() || user?.email || '';

const noteAuthorName = (note: ClinicalNote) => personName(note.createdBy) || 'Unknown';

const noteInitials = (name: string) =>
  name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

const NOTES_PAGE_SIZE = 10;

const ClinicalNotes = ({ patientId, onlyPinnedNotes = false }: ClinicalNotesProps) => {
  const [selectedTab, setSelectedTab] = useState<NotesTabType>(NotesTabType.All);
  const [notesPage, setNotesPage] = useState(1);
  const [localNotes, setLocalNotes] = useState<ClinicalNote[]>([]);
  const [localPinnedNotes, setLocalPinnedNotes] = useState<ClinicalNote[]>([]);
  const [localArchivedNotes, setLocalArchivedNotes] = useState<ClinicalNote[]>([]);
  const [selectedNoteToArchive, setSelectedNoteToArchive] = useState<ClinicalNote | null>(null);
  const [selectedNoteToUnarchive, setSelectedNoteToUnarchive] = useState<ClinicalNote | null>(null);
  const [selectedNoteToEdit, setSelectedNoteToEdit] = useState<ClinicalNote | null>(null);
  const [pinLoadingId, setPinLoadingId] = useState('');
  const [stickyLoadingId, setStickyLoadingId] = useState('');
  const [composerText, setComposerText] = useState('');
  const [notesSortDesc, setNotesSortDesc] = useState(true);

  const processedNotesRef = useRef('');
  const processedPinnedNotesRef = useRef('');

  const [editNoteModalOpened, { open: openEditNoteModal, close: closeEditNoteModal }] =
    useDisclosure(false);
  const [archiveModalOpened, { open: openArchiveModal, close: closeArchiveModal }] =
    useDisclosure(false);
  const [unarchiveModalOpened, { open: openUnarchiveModal, close: closeUnarchiveModal }] =
    useDisclosure(false);

  const {
    data: notesData = { notes: [] },
    isLoading: isNotesLoading,
    refetch: refetchNotes
  } = useGetNotesByPatientIdQuery(
    { patientId, limit: onlyPinnedNotes ? 1 : NOTES_PAGE_SIZE, page: notesPage },
    { skip: !patientId || onlyPinnedNotes, refetchOnMountOrArgChange: true }
  );

  const { data: pinnedNotesData, refetch: refetchPinnedNotes } = useGetPinnedNotesQuery(
    { patientId },
    { skip: !patientId, refetchOnMountOrArgChange: true }
  );

  const [archiveNote, { isLoading: isArchiving }] = useArchiveNoteMutation();
  const [unArchiveNote, { isLoading: isUnarchiving }] = useUnArchiveNoteMutation();
  const [updateNote] = useUpdateNoteMutation();
  const [createNote, { isLoading: isCreatingNote }] = useCreateNoteMutation();

  useEffect(() => {
    if (!notesData?.notes) return;
    const notesHash = JSON.stringify(
        notesData.notes.map((note) => ({
        id: note.id,
        content: note.content,
        updatedAt: note.updatedAt,
        isArchived: note.isArchived,
        isPinned: note.isPinned,
        isSticky: note.isSticky
      }))
    );
    if (processedNotesRef.current === notesHash) return;
    processedNotesRef.current = notesHash;
    setLocalNotes(notesData.notes.filter((note) => !note.isArchived));
    setLocalArchivedNotes(notesData.notes.filter((note) => note.isArchived));
  }, [notesData]);

  useEffect(() => {
    if (!pinnedNotesData?.pinnedNotes) return;
    const pinnedHash = JSON.stringify(
      pinnedNotesData.pinnedNotes.map((note) => ({
        id: note.id,
        content: note.content,
        updatedAt: note.updatedAt,
        isArchived: note.isArchived,
        isPinned: note.isPinned,
        isSticky: note.isSticky
      }))
    );
    if (processedPinnedNotesRef.current === pinnedHash) return;
    processedPinnedNotesRef.current = pinnedHash;
    setLocalPinnedNotes(pinnedNotesData.pinnedNotes.filter((note) => !note.isArchived));
  }, [pinnedNotesData]);

  const showNotification = (title: string, message: string, color: string) => {
    notifications.show({ title, message, color });
  };

  const refreshNotesData = async () => {
    await Promise.all([refetchNotes(), refetchPinnedNotes()]);
  };

  const handleAddInlineNote = async () => {
    const content = composerText.trim();
    if (!content || isCreatingNote) return;
    try {
      await createNote({ patientId, content, source: NoteSource.INTERNAL }).unwrap();
      setComposerText('');
      await refreshNotesData();
    } catch {
      showNotification('Error', 'Failed to add note', 'red');
    }
  };

  const getFilteredNotes = () => {
    switch (selectedTab) {
      case NotesTabType.Pinned:
        return localPinnedNotes.filter((note) => !note.isArchived);
      case NotesTabType.Archived:
        return localArchivedNotes;
      default:
        return localNotes;
    }
  };

  const isArchivedTab = selectedTab === NotesTabType.Archived;
  const noteCount = 'count' in notesData ? notesData.count : 0;
  const sortedNotes = [...getFilteredNotes()].sort((a, b) => {
    const diff = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    return notesSortDesc ? diff : -diff;
  });
  const stickyNote = localNotes.find((note) => note.isSticky) ?? null;

  const handleEditClick = (note: ClinicalNote) => {
    setSelectedNoteToEdit(note);
    openEditNoteModal();
  };

  const handleConfirmArchive = async () => {
    if (!selectedNoteToArchive) return;
    try {
      await archiveNote({ noteId: selectedNoteToArchive.id, patientId }).unwrap();
      if (selectedNoteToArchive.isPinned) {
        setLocalPinnedNotes((prev) => prev.filter((note) => note.id !== selectedNoteToArchive.id));
      }
      setLocalNotes((prev) => prev.filter((note) => note.id !== selectedNoteToArchive.id));
      setLocalArchivedNotes((prev) => [...prev, { ...selectedNoteToArchive, isArchived: true }]);
      await refreshNotesData();
      showNotification('Success', 'Note archived successfully', 'green');
      closeArchiveModal();
      setSelectedNoteToArchive(null);
    } catch {
      showNotification('Error', 'Failed to archive note. Please try again.', 'red');
    }
  };

  const handleConfirmUnarchive = async () => {
    if (!selectedNoteToUnarchive) return;
    try {
      await unArchiveNote({ noteId: selectedNoteToUnarchive.id, patientId }).unwrap();
      const unarchivedNote = { ...selectedNoteToUnarchive, isArchived: false };
      setLocalArchivedNotes((prev) => prev.filter((note) => note.id !== selectedNoteToUnarchive.id));
      setLocalNotes((prev) => [...prev, unarchivedNote]);
      if (selectedNoteToUnarchive.isPinned) {
        setLocalPinnedNotes((prev) => [...prev, unarchivedNote]);
      }
      await refreshNotesData();
      showNotification('Success', 'Note unarchived successfully', 'green');
      closeUnarchiveModal();
      setSelectedNoteToUnarchive(null);
    } catch {
      showNotification('Error', 'Failed to unarchive note. Please try again.', 'red');
    }
  };

  const handleTogglePin = async (clinicalNote: ClinicalNote) => {
    if (pinLoadingId === clinicalNote.id) return;
    setPinLoadingId(clinicalNote.id);
    const isUnpinning = clinicalNote.isPinned;
    const shouldClearSticky = isUnpinning && clinicalNote.isSticky;
    try {
      await updateNote({
        noteId: clinicalNote.id,
        isPinned: !isUnpinning,
        ...(shouldClearSticky && { isSticky: false }),
        patientId
      }).unwrap();
      const updatedFields = {
        isPinned: !isUnpinning,
        ...(shouldClearSticky && { isSticky: false })
      };
      setLocalNotes((prev) =>
        prev.map((note) => (note.id === clinicalNote.id ? { ...note, ...updatedFields } : note))
      );
      if (isUnpinning) {
        setLocalPinnedNotes((prev) => prev.filter((note) => note.id !== clinicalNote.id));
      } else {
        setLocalPinnedNotes((prev) => [...prev, { ...clinicalNote, ...updatedFields }]);
      }
      await refreshNotesData();
      showNotification('Success', `Note ${isUnpinning ? 'unpinned' : 'pinned'} successfully`, 'green');
    } catch {
      showNotification('Error', `Failed to ${isUnpinning ? 'unpin' : 'pin'} note. Please try again.`, 'red');
    } finally {
      setPinLoadingId('');
    }
  };

  const handleToggleSticky = async (clinicalNote: ClinicalNote) => {
    if (stickyLoadingId === clinicalNote.id) return;
    setStickyLoadingId(clinicalNote.id);
    const isRemoving = clinicalNote.isSticky;
    try {
      await updateNote({
        noteId: clinicalNote.id,
        isSticky: !isRemoving,
        isPinned: isRemoving ? clinicalNote.isPinned : true,
        patientId
      }).unwrap();
      const updatedFields = {
        isSticky: !isRemoving,
        isPinned: isRemoving ? clinicalNote.isPinned : true
      };
      setLocalNotes((prev) =>
        prev.map((note) => (note.id === clinicalNote.id ? { ...note, ...updatedFields } : note))
      );
      if (!isRemoving && !clinicalNote.isPinned) {
        setLocalPinnedNotes((prev) => [...prev, { ...clinicalNote, ...updatedFields }]);
      } else {
        setLocalPinnedNotes((prev) =>
          prev.map((note) => (note.id === clinicalNote.id ? { ...note, ...updatedFields } : note))
        );
      }
      await refreshNotesData();
      showNotification(
        'Success',
        `Note ${isRemoving ? 'unstickied' : 'marked as sticky'} successfully`,
        'green'
      );
    } catch {
      showNotification(
        'Error',
        `Failed to ${isRemoving ? 'remove sticky from' : 'mark as sticky'} note. Please try again.`,
        'red'
      );
    } finally {
      setStickyLoadingId('');
    }
  };

  if (!patientId) return <div>No Patient ID provided.</div>;

  const renderNoteCard = (note: ClinicalNote, isArchived = false) => {
    const anotherNoteIsSticky = stickyNote !== null && stickyNote.id !== note.id;
    const name = noteAuthorName(note);

    return (
      <div key={note.id} className={classes.noteItem}>
        <div
          className={classes.noteAvatar}
          style={{ backgroundColor: avatarColor(note.createdBy?.id ?? name) }}>
          {noteInitials(name)}
        </div>
        <div className={classes.noteBody}>
          <Group justify="space-between" align="flex-start" gap={8} wrap="nowrap">
            <Group gap={8} align="baseline" wrap="wrap">
              <Text fw={700} fz="sm" c="plum.9">
                {name}
              </Text>
              <Text fz="xs" c="dimmed">
                {formatDate(note.createdAt, 'MMM D, YYYY, h:mm A')}
              </Text>
              {note.isPinned && (
                <Badge size="xs" variant="light" color="plum" tt="none">
                  Pinned
                </Badge>
              )}
              {note.isSticky && (
                <Badge size="xs" variant="light" color="yellow" tt="none">
                  Sticky
                </Badge>
              )}
            </Group>
            {!onlyPinnedNotes && (
              <Menu position="bottom-end" withinPortal shadow="md" width={190}>
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    radius="md"
                    className={classes.noteMenuTrigger}
                    aria-label="Note actions">
                    <IconDotsVertical size={18} stroke={1.5} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconPencil size={15} stroke={1.5} />}
                    onClick={() => handleEditClick(note)}>
                    Edit
                  </Menu.Item>
                  {!isArchived && (
                    <Menu.Item
                      leftSection={<IconPin size={15} stroke={1.5} />}
                      disabled={pinLoadingId === note.id || !!note.isSticky}
                      onClick={() => handleTogglePin(note)}>
                      {note.isPinned ? 'Unpin' : 'Pin'}
                    </Menu.Item>
                  )}
                  {!isArchived && (
                    <Menu.Item
                      leftSection={
                        note.isSticky ? (
                          <IconBookmarkOff size={15} stroke={1.5} />
                        ) : (
                          <IconBookmark size={15} stroke={1.5} />
                        )
                      }
                      disabled={anotherNoteIsSticky || stickyLoadingId === note.id}
                      onClick={() => handleToggleSticky(note)}>
                      {note.isSticky ? 'Remove sticky' : 'Mark as sticky'}
                    </Menu.Item>
                  )}
                  <Menu.Divider />
                  {isArchived ? (
                    <Menu.Item
                      leftSection={<IconArchiveOff size={15} stroke={1.5} />}
                      onClick={() => {
                        setSelectedNoteToUnarchive(note);
                        openUnarchiveModal();
                      }}>
                      Unarchive
                    </Menu.Item>
                  ) : (
                    <Menu.Item
                      color="red"
                      leftSection={<IconArchive size={15} stroke={1.5} />}
                      disabled={note.isSticky}
                      onClick={() => {
                        if (note.isSticky) return;
                        setSelectedNoteToArchive(note);
                        openArchiveModal();
                      }}>
                      Archive
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
          <div className={classes.noteBubble}>{note.content}</div>
          {note.updatedBy && note.updatedAt && (
            <Text fz="11px" c="dimmed" mt={4}>
              Last updated by {personName(note.updatedBy)} ·{' '}
              {formatDate(note.updatedAt, 'MMM D, YYYY, h:mm A')}
            </Text>
          )}
        </div>
      </div>
    );
  };

  const renderFeed = (notes: ClinicalNote[], isArchived = false) => {
    if (isNotesLoading) {
      return (
        <Box py="md">
          <SkeletonGrid columns={1} rows={3} height={48} />
        </Box>
      );
    }
    if (notes.length === 0) {
      return (
        <Text className={classes.feedEmpty} ta="center" py="xl">
          No notes found
        </Text>
      );
    }
    return <div className={classes.noteFeed}>{notes.map((note) => renderNoteCard(note, isArchived))}</div>;
  };

  if (onlyPinnedNotes) {
    return (
      <div className={classes.reportSection}>
        <div className={classes.pinnedNotesHeader}>
          <TextWrapper text="Pinned Notes" variant="heading" />
        </div>
        <ScrollArea h={localPinnedNotes.length === 0 ? 150 : 280} scrollbarSize={5}>
          {renderFeed(localPinnedNotes)}
        </ScrollArea>
      </div>
    );
  }

  return (
    <Box className={classes.notesPanel}>
      <Box className={classes.notesTabsBar}>
        <Group justify="space-between" align="center" wrap="nowrap">
          <Tabs
            variant="none"
            value={selectedTab}
            onChange={(value) => {
              setSelectedTab(value as NotesTabType);
              setNotesPage(1);
            }}>
            <Tabs.List>
              <Tabs.Tab value={NotesTabType.All} className={classes.tab}>
                All
              </Tabs.Tab>
              <Tabs.Tab value={NotesTabType.Pinned} className={classes.tab}>
                Pinned
              </Tabs.Tab>
              <Tabs.Tab value={NotesTabType.Archived} className={classes.tab}>
                Archived
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>
          <Button
            variant="subtle"
            color="gray"
            size="compact-xs"
            leftSection={<IconArrowsSort size={14} stroke={1.5} />}
            onClick={() => setNotesSortDesc((value) => !value)}>
            {notesSortDesc ? 'Newest first' : 'Oldest first'}
          </Button>
        </Group>
      </Box>

      {renderFeed(sortedNotes, isArchivedTab)}
      {selectedTab !== NotesTabType.Pinned && noteCount > NOTES_PAGE_SIZE && (
        <Group justify="center" mt="sm">
          <Pagination
            total={Math.ceil(noteCount / NOTES_PAGE_SIZE)}
            value={notesPage}
            onChange={setNotesPage}
            size="sm"
            color="plum"
          />
        </Group>
      )}

      <div className={classes.composer}>
        <Textarea
          placeholder="Add a note. Press Cmd or Ctrl and Enter to save."
          value={composerText}
          onChange={(event) => setComposerText(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              handleAddInlineNote();
            }
          }}
          autosize
          minRows={2}
          maxRows={6}
          maxLength={255}
          disabled={isCreatingNote}
        />
        <Group justify="flex-end" mt={8}>
          <Button size="xs" onClick={handleAddInlineNote} loading={isCreatingNote} disabled={!composerText.trim()}>
            Post Note
          </Button>
        </Group>
      </div>

      <AddNotesModal
        isOpen={editNoteModalOpened}
        onClose={() => {
          closeEditNoteModal();
          setSelectedNoteToEdit(null);
        }}
        patientId={patientId}
        noteToEdit={selectedNoteToEdit}
      />

      <ConfirmationModal
        isOpen={archiveModalOpened}
        isLoading={isArchiving}
        onClose={closeArchiveModal}
        onConfirm={handleConfirmArchive}
        title="Archive this note?"
        primaryButtonText="Yes, Archive"
        description="This note will be moved to Archived. You can restore it later from the Archived notes section."
      />

      <ConfirmationModal
        isOpen={unarchiveModalOpened}
        isLoading={isUnarchiving}
        onClose={closeUnarchiveModal}
        onConfirm={handleConfirmUnarchive}
        title="Unarchive this note?"
        primaryButtonText="Yes, Unarchive"
        description="This note will be restored to your active notes."
      />
    </Box>
  );
};

export default ClinicalNotes;
